import styled, { css } from "styled-components";

export const checkedInColors = {
    black: "#000000",
    white: "#FFFFFF",
    espresso: "#2A1E14",
    butter: "#F0D98A",
    cream: "#F5EDD5",
    tomato: "#C3402B",
    secondary: "#8C6844"
};

export const checkedInFonts = css`
    @font-face {
        font-family: "Instrument Serif";
        src: url('/fonts/InstrumentSerif/InstrumentSerif-Regular.ttf') format('truetype');
        font-display: swap;
    }
    @font-face {
        font-family: "Instrument Serif";
        src: url('/fonts/InstrumentSerif/InstrumentSerif-Italic.ttf') format('truetype');
        font-style: italic;
        font-display: swap;
    }
    @font-face {
        font-family: Archivo;
        src: url('/fonts/Archivo/Archivo[wdth,wght].ttf') format('truetype');
        font-weight: 100 900;
        font-display: swap;
    }
    @font-face {
        font-family: "DM Mono";
        src: url('/fonts/DMMono/DMMono-Regular.ttf') format('truetype');
        font-display: swap;
    }
`;

export const CheckedInPage = styled.main<{ $dark?: boolean }>`
    ${checkedInFonts}
    background: ${p => p.$dark ? checkedInColors.espresso : checkedInColors.cream};
    color: ${p => p.$dark ? checkedInColors.cream : checkedInColors.espresso};
    font-family: Archivo, sans-serif;
    font-weight: 400;
    min-height: 100vh;
    min-height: 100dvh;
    width: 100%;
    padding: max(28px, env(safe-area-inset-top)) 24px max(44px, env(safe-area-inset-bottom));
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    *, *::before, *::after { box-sizing: border-box; }
    button, input, select { font-family: inherit; }
    button, a, input, select { -webkit-tap-highlight-color: transparent; }
    :focus-visible { outline: 3px solid ${checkedInColors.tomato}; outline-offset: 4px; }
`;

export const CheckedInContent = styled.div<{ $landing?: boolean }>`
    width: ${p => p.$landing ? "80%" : "100%"};
    max-width: ${p => p.$landing ? "440px" : "520px"};
    flex: 1;
    display: flex;
    flex-direction: column;
`;

export const CheckedInForm = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
`;

export const CheckedInTitle = styled.h1<{ $landing?: boolean; $center?: boolean }>`
    font-family: "Instrument Serif", Georgia, serif;
    font-size: ${p => p.$landing ? "clamp(44px, 12vw, 68px)" : "clamp(36px, 9.5vw, 56px)"};
    font-weight: 400;
    text-align: ${p => p.$center ? "center" : "left"};
    line-height: 1.18;
    letter-spacing: -0.025em;
    margin: 20px 0 28px;
    em { color: ${p => p.$landing ? checkedInColors.butter : checkedInColors.tomato}; }
`;

export const CheckedInBody = styled.p<{ $center?: boolean }>`
    color: ${checkedInColors.secondary};
    font-size: 16px;
    line-height: 1.3;
    margin: 0 0 28px;
    text-align: ${p => p.$center ? "center" : "left"};
    overflow-wrap: anywhere;
`;

export const CheckedInLabel = styled.label`
    display: block;
    color: ${checkedInColors.secondary};
    font-family: "DM Mono", monospace;
    font-size: 14px;
    text-transform: uppercase;
    margin: 0 0 12px;
`;

export const CheckedInEyebrow = styled.p<{ $center?: boolean }>`
    color: ${checkedInColors.tomato};
    font-family: "DM Mono", monospace;
    font-size: 14px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    text-align: ${p => p.$center ? "center" : "left"};
    margin: 32px 0 0;
`;

export const CheckedInInput = styled.input`
    background: #fff;
    color: ${checkedInColors.espresso};
    width: 100%;
    min-width: 0;
    height: 62px;
    border: 1px solid ${checkedInColors.secondary};
    border-radius: 14px;
    font-size: 18px;
    padding: 16px;
    margin: 0 0 28px;
    &::placeholder { color: ${checkedInColors.secondary}; opacity: 1; }
`;

export const CheckedInButton = styled.button<{ $light?: boolean; $outline?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 48px;
    padding: 13px 20px;
    border-radius: 999px;
    border: 1px solid ${p => p.$outline ? checkedInColors.cream : "transparent"};
    background: ${p => p.$outline ? "transparent" : p.$light ? checkedInColors.cream : checkedInColors.espresso};
    color: ${p => p.$light ? checkedInColors.espresso : checkedInColors.cream};
    font-size: 18px;
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;
    &:disabled { opacity: 0.45; cursor: default; }
`;

export const CheckedInActions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-top: auto;
    padding-top: 32px;
`;

export const CheckedInLink = styled.a`
    color: ${checkedInColors.espresso};
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 2px;
`;

export const CheckedInTextButton = styled.button`
    background: none;
    border: 0;
    color: ${checkedInColors.espresso};
    font-size: inherit;
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 2px;
    padding: 0;
    cursor: pointer;
    &:disabled { opacity: 0.45; cursor: default; }
`;

export const CheckedInBack = styled.button`
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    padding: 0;
    margin: 0 0 6px -8px;
    background: none;
    border: 0;
    color: ${checkedInColors.espresso};
    cursor: pointer;
    svg { width: 24px; height: 28px; }
`;

export const CheckedInCodeRow = styled.div`
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 4px;
    margin: 0 0 28px;
    input {
        width: 100%;
        min-width: 0;
        height: clamp(54px, 15vw, 72px);
        border: 1px solid ${checkedInColors.secondary};
        border-radius: 14px;
        color: ${checkedInColors.espresso};
        background: #fff;
        font-size: 26px;
        text-align: center;
        padding: 0;
    }
`;

export const CheckedInNotice = styled.p<{ $error?: boolean }>`
    color: ${p => p.$error ? checkedInColors.tomato : checkedInColors.secondary};
    font-size: 15px;
    line-height: 1.4;
    margin: 0 0 16px;
`;

export const CheckedInLogo = styled.img`
    width: 100%;
    max-width: 100%;
    height: auto;
    align-self: center;
    margin: 36px 0 0;
`;

export const CheckedInWordmark = styled.div`
    width: 225px;
    max-width: 100%;
    overflow: hidden;
    margin: 12px 0 4px;
    img {
        display: block;
        width: 118%;
        max-width: none;
        margin-left: -18%;
        filter: brightness(0) saturate(100%) invert(10%) sepia(13%) saturate(1804%)
            hue-rotate(345deg) brightness(94%) contrast(93%);
    }
`;

export const CheckedInHero = styled.div`
    padding: clamp(60px, 12vh, 130px) 0 32px;
    p { color: ${checkedInColors.cream}; font-size: 16px; line-height: 1.4; margin-bottom: 0; }
`;
